/**
 * Mode 01 PID definitions + decoding. Formulas per SAE J1979 (OBD-II PIDs).
 *
 * Each PID's `decode` takes the data bytes (A, B, ...) that follow the
 * `41 <pid>` response header and returns the engineering value in `unit`.
 * Closures destructure with defaults so they are safe under
 * `noUncheckedIndexedAccess`; `parsePidResponse` still validates byte count
 * before calling.
 */
function round2(n) {
    return Math.round(n * 100) / 100;
}
export const PID_DEFINITIONS = {
    '04': { id: '04', key: 'engineLoad', name: 'Engine load', unit: '%', bytes: 1, decode: ([a = 0]) => (100 / 255) * a },
    '05': {
        id: '05',
        key: 'coolantTemp',
        name: 'Coolant temperature',
        unit: '°C',
        bytes: 1,
        decode: ([a = 0]) => a - 40,
    },
    '06': {
        id: '06',
        key: 'shortFuelTrim1',
        name: 'Short-term fuel trim',
        unit: '%',
        bytes: 1,
        decode: ([a = 0]) => (100 / 128) * a - 100,
    },
    '07': {
        id: '07',
        key: 'longFuelTrim1',
        name: 'Long-term fuel trim',
        unit: '%',
        bytes: 1,
        decode: ([a = 0]) => (100 / 128) * a - 100,
    },
    '0A': { id: '0A', key: 'fuelPressure', name: 'Fuel pressure', unit: 'kPa', bytes: 1, decode: ([a = 0]) => 3 * a },
    '0B': {
        id: '0B',
        key: 'intakeManifoldPressure',
        name: 'Intake manifold pressure',
        unit: 'kPa',
        bytes: 1,
        decode: ([a = 0]) => a,
    },
    '0C': {
        id: '0C',
        key: 'engineRpm',
        name: 'Engine RPM',
        unit: 'rpm',
        bytes: 2,
        decode: ([a = 0, b = 0]) => (256 * a + b) / 4,
    },
    '0D': { id: '0D', key: 'vehicleSpeed', name: 'Vehicle speed', unit: 'km/h', bytes: 1, decode: ([a = 0]) => a },
    '0F': {
        id: '0F',
        key: 'intakeAirTemp',
        name: 'Intake air temperature',
        unit: '°C',
        bytes: 1,
        decode: ([a = 0]) => a - 40,
    },
    '10': {
        id: '10',
        key: 'mafRate',
        name: 'Mass air flow',
        unit: 'g/s',
        bytes: 2,
        decode: ([a = 0, b = 0]) => (256 * a + b) / 100,
    },
    '11': {
        id: '11',
        key: 'throttlePosition',
        name: 'Throttle position',
        unit: '%',
        bytes: 1,
        decode: ([a = 0]) => (100 / 255) * a,
    },
    '1F': {
        id: '1F',
        key: 'runTimeSinceStart',
        name: 'Run time since engine start',
        unit: 's',
        bytes: 2,
        decode: ([a = 0, b = 0]) => 256 * a + b,
    },
    '21': {
        id: '21',
        key: 'distanceWithMil',
        name: 'Distance with warning light on',
        unit: 'km',
        bytes: 2,
        decode: ([a = 0, b = 0]) => 256 * a + b,
    },
    '2F': {
        id: '2F',
        key: 'fuelLevel',
        name: 'Fuel tank level',
        unit: '%',
        bytes: 1,
        decode: ([a = 0]) => (100 / 255) * a,
    },
    '31': {
        id: '31',
        key: 'distanceSinceCodesCleared',
        name: 'Distance since codes cleared',
        unit: 'km',
        bytes: 2,
        decode: ([a = 0, b = 0]) => 256 * a + b,
    },
    '42': {
        id: '42',
        key: 'controlModuleVoltage',
        name: 'Control module voltage',
        unit: 'V',
        bytes: 2,
        decode: ([a = 0, b = 0]) => (256 * a + b) / 1000,
    },
    '46': {
        id: '46',
        key: 'ambientAirTemp',
        name: 'Ambient air temperature',
        unit: '°C',
        bytes: 1,
        decode: ([a = 0]) => a - 40,
    },
    '5C': {
        id: '5C',
        key: 'engineOilTemp',
        name: 'Engine oil temperature',
        unit: '°C',
        bytes: 1,
        decode: ([a = 0]) => a - 40,
    },
};
/** All PID ids in a stable read order (lightest/most useful first). */
export const DEFAULT_SCAN_PIDS = [
    '0C',
    '0D',
    '05',
    '2F',
    '42',
    '04',
    '11',
    '0F',
    '46',
    '5C',
    '10',
    '0B',
    '06',
    '07',
    '0A',
    '1F',
    '21',
    '31',
];
/**
 * Parse a Mode 01 PID response (cleaned bytes) into a reading.
 * Expects `41 <pid> <data...>`. Returns null on header mismatch or short data.
 */
export function parsePidResponse(pidId, bytes) {
    const def = PID_DEFINITIONS[pidId];
    if (!def)
        return null;
    if (bytes[0] !== 0x41)
        return null; // Mode 01 positive response
    const pidByte = parseInt(pidId, 16);
    if (bytes[1] !== pidByte)
        return null; // PID echo mismatch (wrong/garbled frame)
    const data = bytes.slice(2, 2 + def.bytes);
    if (data.length < def.bytes)
        return null;
    return { id: def.id, key: def.key, name: def.name, value: round2(def.decode(data)), unit: def.unit };
}
//# sourceMappingURL=pids.js.map